import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import { fetchStanTabel, usunZnanaRoznice, ustawZnanaRoznice, zlecSpisTabel } from '@/lib/sync-tables-api';
import { fetchTabeleDoUzgodnienia } from '@/lib/uzgadnianie-api';
import { OdczytTabeli } from '@/components/OdczytTabeli';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  STAN SYNCHRONIZACJI WSZYSTKICH TABEL — portal kontra węzły.
 *
 *  ⚠️⚠️ ZAMÓWIONE 21.09.2026: „chciałem sprawdzać stan synchronizacji WSZYSTKICH tabel, a nie
 *  tylko drawersów". Miara zajętości odpowiada na jedno pytanie o jedną tabelę; tu chodzi o widok
 *  całej wyspy — gdzie read-model portalu rozjechał się z bazami klienta.
 *
 *  ⚠️⚠️ NIC TU NIE PYTAMY MASZYN i to jest cała zaleta tego ekranu: liczby węzłów przychodzą
 *  czujnikiem geometrii PRZY KAŻDYM CYKLU, a liczby portalu umie policzyć read-model. Obie strony
 *  leżały w bazie od dawna — brakowało wyłącznie miejsca, w którym stoją obok siebie.
 *
 *  ⚠️⚠️ „NIE WIEMY" MUSI WYGLĄDAĆ INACZEJ NIŻ „ZGODNE". Czujnik obejmuje tylko tabele per automat,
 *  więc kadra, towary i limity wypadną jako niezmierzone. Pokazanie ich na zielono byłoby dokładnie
 *  tym błędem, przez który ekran rozjazdu przez trzy dni uspokajał, porównując węzły wyłącznie
 *  ze sobą — a pytanie dotyczyło portalu, którego w porównaniu w ogóle nie było.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
/** Różnica ze znakiem — „+21” czyta się jak „bazy mają więcej”, samo „21” nie mówi, w którą stronę. */
const zeZnakiem = (n: number): string => (n > 0 ? `+${n}` : String(n));

export function StanTabel({
  groupId,
  onUzgodnij,
  rozjazdMiedzyWezlami = null,
  onPokazRozjazd,
}: {
  groupId: string;
  /** „Uzgodnij” przy tabeli — ekran ustawia ją w przebiegu niżej i tam przewija. */
  onUzgodnij?: (sps: string) => void;
  /** Rozjazd węzłów MIĘDZY SOBĄ (czujnik geometrii) — `null` albo zera = nie ma czego pokazać. */
  rozjazdMiedzyWezlami?: { tabel: number; automatow: number } | null;
  onPokazRozjazd?: () => void;
}) {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const toast = useToast();
  const qc = useQueryClient();
  const [zlecam, setZlecam] = useState(false);
  /** Tabela rozwinięta do odczytu wiersz po wierszu — jedna naraz, żeby ekran nie puchł. */
  const [rozwinieta, setRozwinieta] = useState<string | null>(null);
  /** Automat do odczytu tabeli per automat — pusty = jeszcze nie wybrany. */
  const [automat, setAutomat] = useState('');
  const [uznaje, setUznaje] = useState(false);

  /**
   * ⚠️ TEN SAM KLUCZ CO W PRZEBIEGU — jedna odpowiedź obu panelom: lista automatów do odczytu
   * i tabele, które przebieg w ogóle umie uzgodnić (tylko przy nich stoi „Uzgodnij”).
   */
  const slownik = useQuery({
    queryKey: ['uzgadnianie-tabele', groupId],
    queryFn: () => fetchTabeleDoUzgodnienia(groupId),
    enabled: groupId !== '',
  });
  const automaty = slownik.data?.automaty ?? [];
  const doUzgodnienia = new Set((slownik.data?.tabele ?? []).map((x) => x.sps.toLowerCase()));

  const dane = useQuery({
    queryKey: ['stan-tabel', groupId],
    queryFn: () => fetchStanTabel(groupId),
    enabled: groupId !== '',
    refetchInterval: 60_000,
  });

  const tabele = dane.data?.tabele ?? [];

  const policz = async (): Promise<void> => {
    setZlecam(true);
    try {
      const w = await zlecSpisTabel(groupId);
      toast.success(t('stanTabel.zlecono').replace('{n}', String(w.wezly.length)));
      /** ⚠️ Węzły pominięte przez starą wersję muszą paść z nazwy — ich brak w wyniku czyta się jak zgoda. */
      if (w.zaStare.length > 0) toast.toast(t('stanTabel.zaStare').replace('{wezly}', w.zaStare.join(', ')));
      await qc.invalidateQueries({ queryKey: ['stan-tabel', groupId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setZlecam(false);
    }
  };

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ZNANA RÓŻNICA — człowiek uznaje rozjazd za wyjaśniony (np. sieroty w SPS).
   *
   *  ⚠️⚠️ ZAMÓWIONE 23.09.2026 (IKEA): dwie tabele świeciły na czerwono na zawsze, bo portal z kluczami
   *  obcymi nie przyjmie wierszy wskazujących na nieistniejący rekord. Portal tego sam nie wykryje —
   *  odrzuconych sierot nie trzyma — więc decyzja jest człowieka, a reguła po stronie serwera pilnuje,
   *  żeby nie przykryła nowego rozjazdu (`stan-tabel.ts`).
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const uznaj = async (sps: string, roznica: number): Promise<void> => {
    const tresc = t('stanTabel.znanaPytanie').replace('{n}', zeZnakiem(roznica)).replace('{tabela}', sps);
    if (!(await zapytaj({ tytul: t('stanTabel.znanaRoznica'), tresc, etykietaPotwierdz: t('stanTabel.znanaRoznica') }))) return;
    setUznaje(true);
    try {
      await ustawZnanaRoznice(groupId, sps, roznica);
      await qc.invalidateQueries({ queryKey: ['stan-tabel', groupId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setUznaje(false);
    }
  };

  const cofnij = async (sps: string): Promise<void> => {
    setUznaje(true);
    try {
      await usunZnanaRoznice(groupId, sps);
      await qc.invalidateQueries({ queryKey: ['stan-tabel', groupId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setUznaje(false);
    }
  };

  if (tabele.length === 0) return null;

  /** ⚠️ Rozjazdy na górę: to jedyne wiersze, po których cokolwiek się robi. */
  const kolejnosc = { ROZJAZD: 0, NIE_WIEMY: 1, ZNANA_ROZNICA: 2, PODZBIOR: 3, ZGODNE: 4 } as const;
  const posortowane = [...tabele].sort((a, b) => kolejnosc[a.stan] - kolejnosc[b.stan]);

  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      {oknoPytania}
      <h2 className="text-sm font-medium">{t('stanTabel.tytul')}</h2>
      <p className="text-[11px] text-muted-foreground">{t('stanTabel.poCo')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={zlecam} onClick={() => void policz()}>
          {zlecam ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          {t('stanTabel.policz')}
        </Button>
        {dane.data?.spisCzeka === true && (
          <span className="text-[11px] text-muted-foreground">{t('stanTabel.czekam')}</span>
        )}
        {/*
          ⚠️ SKRÓT DO OKNA „CO DOKŁADNIE JEST ROZJECHANE" — do 23.09.2026 otwierało się wyłącznie
          z kroku 7 wdrożenia, czyli z zakładki, do której na działającej wyspie nikt nie zagląda.
          Tamto okno mówi o węzłach MIĘDZY SOBĄ, ta lista o portalu kontra bazy — dwa różne pytania.
        */}
        {rozjazdMiedzyWezlami !== null && rozjazdMiedzyWezlami.tabel > 0 && onPokazRozjazd !== undefined && (
          <span className="flex items-center gap-1 text-[11px] text-[var(--warning)]">
            {t('stanTabel.miedzyWezlami')
              .replace('{tabel}', String(rozjazdMiedzyWezlami.tabel))
              .replace('{automatow}', String(rozjazdMiedzyWezlami.automatow))}
            <Button size="sm" variant="ghost" className="h-5 px-1 text-[11px]" onClick={onPokazRozjazd}>
              <Search className="size-3" />
              {t('rozj.pokaz')}
            </Button>
          </span>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        {t('stanTabel.podsumowanie')
          .replace('{n}', String(dane.data?.mierzonych ?? 0))
          .replace('{razem}', String(tabele.length))}
      </p>

      <ul className="space-y-1">
        {posortowane.map((x) => (
          <li key={x.sps} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs">
            <span className="min-w-64 font-mono text-[11px]">{x.sps}</span>

            <span className="tabular-nums">
              {x.portal === null ? t('stanTabel.brakLicznika') : t('stanTabel.portal').replace('{n}', String(x.portal))}
            </span>

            {/*
              ⚠️⚠️ TRZY STANY, TRZY KOLORY I TRZY RÓŻNE ZDANIA. Zlanie „nie wiemy" z „zgodne"
              w jedną zieleń jest jedynym sposobem, w jaki ten ekran może zaszkodzić: uspokoiłby
              tam, gdzie po prostu nie patrzy.
            */}
            <span
              className={cn(
                'font-medium',
                x.stan === 'ROZJAZD' && 'text-[var(--destructive)]',
                x.stan === 'ZGODNE' && 'text-[var(--success)]',
                x.stan === 'NIE_WIEMY' && 'text-muted-foreground',
                x.stan === 'PODZBIOR' && 'text-muted-foreground',
                x.stan === 'ZNANA_ROZNICA' && 'text-muted-foreground',
              )}
            >
              {x.stan === 'ROZJAZD' && t('stanTabel.rozjazd').replace('{wezly}', x.rozjechane.join(', '))}
              {x.stan === 'ZGODNE' && t('stanTabel.zgodne').replace('{n}', String(x.wezly.length))}
              {/*
                ⚠️⚠️ „PORTAL TRZYMA CZĘŚĆ" TO NIE JEST ROZJAZD i nie wolno tego tak pokazać.
                Złapane na żywej wyspie: `SetupData` — portal 156, węzły 9322, bo portal czyta
                parametry z białej listy. Czerwony napis zaprosiłby do „naprawy", która wciągnęłaby
                dziewięć tysięcy wierszy, o które nikt nie prosi.
              */}
              {x.stan === 'PODZBIOR' && `${t('stanTabel.podzbior')} — ${x.podzbior ?? ''}`}
              {x.stan === 'ZNANA_ROZNICA' &&
                x.znana != null &&
                t('stanTabel.znana')
                  .replace('{n}', zeZnakiem(x.znana.roznica))
                  .replace('{kto}', x.znana.kto)
                  .replace('{kiedy}', new Date(x.znana.kiedy).toLocaleString('pl'))}
              {x.stan === 'NIE_WIEMY' &&
                `${t('stanTabel.nieWiemy')} — ${
                  !x.mierzona
                    ? t('stanTabel.nieMierzona')
                    : x.portal === null
                      ? t('stanTabel.brakLicznika')
                      : t('stanTabel.brakMeldunkow')
                }`}
            </span>

            {/*
              ⚠️⚠️ SKĄD POCHODZI LICZBA WĘZŁA — czujnik jedzie co cykl i obejmuje tylko tabele
              per automat, spis idzie na żądanie i obejmuje wszystko. Bez tej etykiety dwie liczby
              z dwóch różnych chwil stałyby obok siebie bez słowa, a ich różnica nazywałaby się
              rozjazdem.
            */}
            {x.zrodlo !== null && (
              <span className="text-[11px] text-muted-foreground">
                {x.zrodlo === 'ODCZYT' ? t('stanTabel.zOdczytu') : t('stanTabel.zCzujnika')}
              </span>
            )}

            {/*
              ══════════════════════════════════════════════════════════════════════════════════
               PRZYCISKI TYLKO TAM, GDZIE JEST CO ROBIĆ — rozjazd, „nie wiemy" i znana różnica.

               ⚠️⚠️ „WIERSZE" STOI TAKŻE PRZY „NIE WIEMY", i to jest przejęte po panelu „Kadra
               na węzłach" (usuniętym 23.09.2026). Czujnik nie liczy tabel ludzi W OGÓLE, więc przycisk
               pokazywany tylko przy rozjeździe nie pokazałby się przy kadrze nigdy — a brak człowieka
               na rodzeństwie był dokładnie tym, czego tamten panel pilnował (Donaldson, 14.09.2026).

               ⚠️ „WIERSZE" TYLKO DLA TABEL, KTÓRE ZLECENIE ODCZYTU PRZYJMIE (`odczyt` z serwera):
               kadra bez automatu, tabela automatu z automatem. Przy pozostałych zlecenie odmówiłoby.
              ══════════════════════════════════════════════════════════════════════════════════
            */}
            {x.odczyt != null && (x.stan === 'ROZJAZD' || x.stan === 'NIE_WIEMY' || x.stan === 'ZNANA_ROZNICA') && (
              <Button
                size="sm"
                variant="ghost"
                className="h-5 px-1 text-[11px]"
                onClick={() => {
                  setRozwinieta(rozwinieta === x.sps ? null : x.sps);
                  setAutomat('');
                }}
              >
                <Search className="size-3" />
                {t('stanTabel.wiersze')}
              </Button>
            )}
            {x.stan === 'ROZJAZD' && onUzgodnij !== undefined && doUzgodnienia.has(x.sps.toLowerCase()) && (
              <Button size="sm" variant="ghost" className="h-5 px-1 text-[11px]" onClick={() => onUzgodnij(x.sps)}>
                {t('stanTabel.uzgodnij')}
              </Button>
            )}
            {/* ⚠️ Uznać da się tylko różnicę jednolitą — mieszaną trzeba najpierw sprawdzić (serwis). */}
            {x.stan === 'ROZJAZD' && x.roznica != null && (
              <Button
                size="sm"
                variant="ghost"
                className="h-5 px-1 text-[11px]"
                disabled={uznaje}
                onClick={() => void uznaj(x.sps, x.roznica!)}
              >
                {t('stanTabel.znanaRoznica')}
              </Button>
            )}
            {x.znana != null && (x.stan === 'ZNANA_ROZNICA' || x.stan === 'ROZJAZD') && (
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                {/* ⚠️ Uznanie, które już nie pasuje, zostaje na widoku — inaczej „znów czerwone" nie mówi dlaczego. */}
                {x.stan === 'ROZJAZD' && t('stanTabel.znanaByla').replace('{n}', zeZnakiem(x.znana.roznica))}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 px-1 text-[11px]"
                  disabled={uznaje}
                  onClick={() => void cofnij(x.sps)}
                >
                  {t('stanTabel.cofnij')}
                </Button>
              </span>
            )}
            {/* ⚠️ Liczby węzłów przy rozjeździe, bo bez nich „rozjazd" nie mówi, w którą stronę. */}
            {x.stan === 'ROZJAZD' && (
              <span className="basis-full pl-2 font-mono text-[11px] text-muted-foreground">
                {x.wezly.map((w) => `${w.nodeId} ${w.wierszy}`).join(' · ')}
              </span>
            )}
            {rozwinieta === x.sps && x.odczyt != null && (
              <div className="basis-full space-y-1 pl-2">
                {x.odczyt === 'AUTOMAT' && (
                  <select
                    className="h-7 rounded border border-border bg-background px-2 text-xs"
                    value={automat}
                    onChange={(e) => setAutomat(e.target.value)}
                  >
                    <option value="">{t('uzgadnianie.wybierzAutomat')}</option>
                    {automaty.map((a) => (
                      <option key={a.vmid} value={String(a.vmid)}>
                        {a.nazwa} ({a.vmid})
                      </option>
                    ))}
                  </select>
                )}
                {(x.odczyt === 'WYSPA' || automat !== '') && (
                  <OdczytTabeli groupId={groupId} tabela={x.sps} vmid={x.odczyt === 'WYSPA' ? null : Number(automat)} />
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
